/**
 * settlement_proxy — the FAR TIER's data model (settlement study R7 v3, 2026-09-02).
 *
 * Every settlement carries two far representations, baked per world by
 * verify/bake-proxies.mts from its plan (lib/settlement_proxy_bake.ts
 * replicates the resolve step's structure picks and assembly scale):
 *   HLOD-0 `structs` — the resolved structures (key, local pad position, yaw,
 *            scale, seat ground): the client instances the REAL simplified
 *            prefab (LOD1/LOD2, pack-served), PIVOT-SEATED like the assembly
 *            (`seatPlacement`), for settlements within view range;
 *   HLOD-1 `boxes`   — silhouette-ladder boxes (6 height bands) cut from
 *            each prefab's own vertex cloud, drawn for every settlement that
 *            is not built inside the ladder's bounded reach, one instanced
 *            draw, shaded base → crown.
 * Pure helpers here so the harness pins the bake and the visibility rules.
 */
import { planFileFor } from './settlement_plan.js';
import type { PrefabMetrics, ProxyPlacement } from './settlement_proxy_bake.js';
import type { StructureSolid } from './structure_solids.js';

export const PROXY_FORMAT_VERSION = 4;

/** Palette (linear-ish sRGB): 0–2 civ A (Epic Sci-Fi steel), 3–5 civ B (FutureCity cladding + glass). */
export const PROXY_PALETTE: ReadonlyArray<readonly [number, number, number]> = [
  [0.34, 0.37, 0.42], [0.27, 0.30, 0.35], [0.30, 0.42, 0.48],
  [0.80, 0.82, 0.85], [0.68, 0.71, 0.75], [0.56, 0.66, 0.78],
];

/** Numbers per box: lx, lz, w, d, y0, h, yaw, colour, gy (absolute seat ground), tint (0 base → 1 crown: the height shade). */
export const BOX_STRIDE = 10;
/** Numbers per struct: keyIdx, lx, lz, yaw, scale, gy (absolute seat ground). */
export const STRUCT_STRIDE = 6;

export function proxyFileFor(seed: string): string {
  return planFileFor(seed, 0).replace(/-0\.json$/, '.json');
}

export interface ProxyKey { key: string; assetId: string; colour: number; ladder: number[][]; h: number; /** natural-scale bbox centre x, bottom y, centre z (the pivot the seat compensates) */ pivot: [number, number, number] }
export interface ProxySite { id: number; tier: string; cx: number; cz: number; r: number; civ: string; structs: number[]; boxes: number[] }
export interface ProxyFile { v: number; seed: string; palette: ReadonlyArray<readonly [number, number, number]>; keys: ProxyKey[]; sites: ProxySite[] }

// ── The ladder as a COLUMN MAP (structure collision wave, 2026-09-23) ────────
// The far tier's six height bands per prefab are cut from the collider cloud
// (the XZ box of the vertices in each sixth of the height), so they are the
// ready map of where a building actually stands at each altitude: a compound's
// ground band is its whole enclosure, its towers narrow above it. The assembly
// registered ONE base→roof box per building — a ship bounced off empty air 200 m
// above a low compound with one spire. The module publishes the parsed keys here
// the moment the file lands; the assembly and the HLOD-0 ring cut their solids
// per band from them (`ladderSolids`) and keep the single box for a key the
// file does not carry.
let proxyKeyCache: Map<string, ProxyKey> | null = null;

/** Publish the proxies file's keys (SettlementProxyModule, as soon as the file is parsed). */
export function setProxyKeys(keys: ProxyKey[]): void {
  proxyKeyCache = new Map(keys.map((k) => [k.key, k]));
}

/** A prefab key's silhouette ladder — `[y0, h, w, d, ox, oz]` per band, natural units about the bbox
 *  bottom-centre — or null before the proxies file is parsed / for a key it does not carry. */
export function proxyLadder(key: string): number[][] | null {
  return proxyKeyCache?.get(key)?.ladder ?? null;
}

/**
 * ONE SOLID PER LADDER BAND for a placement whose bbox bottom-centre is seated at
 * (wx, gy, wz) with `yaw` and `scale` — exactly the frame `seatPlacement` and the
 * assembly's pivot compensation put the mesh in, and the frame `boxesForPlacements`
 * draws the HLOD-1 boxes in (the same rotation of the band's (ox, oz) excursion):
 * centre = seat + R(yaw)·(ox, oz)·scale, half-extents = (w, d)·scale / 2,
 * base = gy + y0·scale, top = base + h·scale. The bands carry `masked` so a hull on
 * the ground defers to the walking-height mask where one covers (structure_masks).
 */
export function ladderSolids(ladder: number[][], wx: number, wz: number, gy: number, yaw: number, scale: number): StructureSolid[] {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const out: StructureSolid[] = [];
  for (const [y0, h, w, d, ox, oz] of ladder) {
    const rx = ox * c + oz * s;
    const rz = -ox * s + oz * c;
    const base = gy + y0 * scale;
    out.push({ x: wx + rx * scale, z: wz + rz * scale, hx: (w * scale) / 2, hz: (d * scale) / 2, yaw, base, top: base + Math.max(1, h * scale), masked: true });
  }
  return out;
}

/** A key's colour index: its civ band + a stable shade from the key text. */
export function keyColour(key: string, civ: 'a' | 'b'): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619) >>> 0;
  return (civ === 'a' ? 0 : 3) + (h % 3);
}

/**
 * The assembly's PIVOT COMPENSATION (CommonsSettlementsModule, collider wave
 * 2026-07-22), verbatim: the cooked civ GLBs are off-pivot — bbox centres sit
 * 40–1290 m from the model origin and civ-A bases 70–130 m up — so an instance
 * is placed where its measured bbox BOTTOM-CENTRE lands on the pad (wx, gy, wz).
 * Returns the model-origin position to compose the instance matrix with.
 * v2 of the far tier composed the origin at the pad and scattered every
 * prefab scale×offset away (kilometres, and hundreds of metres up).
 */
export function seatPlacement(wx: number, gy: number, wz: number, yaw: number, scale: number, bb: { minX: number; maxX: number; minY: number; minZ: number; maxZ: number }): [number, number, number] {
  const pcx = ((bb.minX + bb.maxX) / 2) * scale;
  const pcz = ((bb.minZ + bb.maxZ) / 2) * scale;
  const pby = bb.minY * scale;
  const c = Math.cos(yaw), s = Math.sin(yaw);
  return [wx - (pcx * c + pcz * s), gy - pby, wz - (-pcx * s + pcz * c)];
}

/**
 * HLOD-1 boxes for a site's placements: each placement's prefab ladder
 * (bands relative to the prefab's bbox bottom-centre), scaled, rotated about
 * the placement and offset to it; each box carries its seat ground. Flat
 * BOX_STRIDE array.
 */
export function boxesForPlacements(placements: ProxyPlacement[], metrics: (key: string) => PrefabMetrics | null, colourOf: (key: string) => number): number[] {
  const out: number[] = [];
  for (const p of placements) {
    const m = metrics(p.key);
    if (!m) continue;
    const c = Math.cos(p.yaw), s = Math.sin(p.yaw);
    const colour = colourOf(p.key);
    const total = Math.max(0.2, m.h);
    for (const [y0, h, w, d, ox, oz] of m.ladder) {
      const tint = +Math.min(1, Math.max(0, (y0 + h / 2) / total)).toFixed(3);
      // Rotate the band's centre offset into the placement frame (y-up, yaw about y).
      const rx = ox * c + oz * s;
      const rz = -ox * s + oz * c;
      out.push(
        +(p.lx + rx * p.scale).toFixed(2), +(p.lz + rz * p.scale).toFixed(2),
        +(w * p.scale).toFixed(2), +(d * p.scale).toFixed(2),
        +(y0 * p.scale).toFixed(2), +Math.max(1, h * p.scale).toFixed(2),
        p.yaw, colour, p.gy, tint,
      );
    }
  }
  return out;
}

/** Validate a decoded proxy file; returns the reason it is unusable, or null. */
export function proxyFileProblem(f: unknown, seed: string): string | null {
  const p = f as Partial<ProxyFile> | null;
  if (!p || typeof p !== 'object') return 'not an object';
  if (p.v !== PROXY_FORMAT_VERSION) return `format v${String(p.v)} ≠ ${PROXY_FORMAT_VERSION}`;
  if (p.seed !== seed) return 'baked for another seed';
  if (!Array.isArray(p.sites) || !Array.isArray(p.palette) || !Array.isArray(p.keys)) return 'missing sites/palette/keys';
  for (const s of p.sites) {
    if (!Array.isArray(s.boxes) || s.boxes.length % BOX_STRIDE !== 0) return `site ${String(s?.id)} boxes malformed`;
    if (!Array.isArray(s.structs) || s.structs.length % STRUCT_STRIDE !== 0) return `site ${String(s?.id)} structs malformed`;
    for (let i = 0; i < s.structs.length; i += STRUCT_STRIDE) if (!(s.structs[i] >= 0 && s.structs[i] < p.keys.length)) return `site ${String(s?.id)} struct key out of range`;
  }
  for (const k of p.keys) if (!Array.isArray(k.pivot) || k.pivot.length !== 3) return `key ${String(k?.key)} has no pivot`;
  return null;
}

/**
 * Visibility with a hysteresis band: shown inside `radius`, kept until
 * `radius + hysteresis` once shown, never while the real set is built. Both
 * far rings use it — HLOD-0 with the ladder's `hlod`, HLOD-1 with its
 * BOUNDED `boxes` reach (a radius of Infinity still means "any distance").
 */
export function proxyVisible(dist: number, built: boolean, shownBefore: boolean, radius: number, hysteresis = 100): boolean {
  if (built) return false;
  if (!Number.isFinite(radius)) return true;
  return shownBefore ? dist <= radius + hysteresis : dist <= radius;
}
